// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import {AttrsSchema, Component, jsx} from 'panel';
import {VNode} from 'snabbdom';

import {Translatable} from 'common/i18n';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {getCurrentMoment, getTimeString, parseDate} from 'common/util/date';
import {emitTrackEvent} from 'common/util/dom';
import {Visibility} from 'common/components/base/stackable';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {DropMenuEl} from 'common/components/drop-menu';
import {TooltipEl} from 'common/components/tooltip';
import {CurrentProject} from 'common/report/model-resources/projects';
import {DateRange, DateRangeAttributes, PresetOption} from 'common/widgets/date-range-picker/models/date-range';
import {DATE_RANGE_TYPES} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DateRangeType, DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {
  generateDateRangeTrackingProps,
  getDatePickerMode,
  getDateRangeLabel,
  isMaxHistoryFinite,
} from 'common/widgets/date-range-picker-new/util';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {DateRangePicker, DateRangePickerEl} from 'common/widgets/date-range-picker-new';
import {getReportName} from 'common/report/util';
import {IronContextRegistry} from 'common/report/context-registry';
import {QueryContextManager} from 'common/query-context';
import {ItemsMenuEl} from 'common/components/items-menu';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {SvgIconEl} from 'common/components/svg-icon';

import {PresetDateRange} from '../date-range-picker/models/presets';
import {getDateRangeTooltipRenderer, getDateRangeTooltipText} from './date-range-tooltip';
import css from './index.inline.styl';

interface DateRangePresetBarState {
  fromTimeText: string;
  isCustomPickerMenuOpen: boolean;
  isTimePickerEnabled: boolean;
  isToDateMenuOpen: boolean;
  selectedPresetIdx: number;
  toTimeText: string;
}

interface DateRangePresetBarAttrs {
  'can-clear': boolean;
  'date-range': Partial<DateRangeAttributes>;
  'disable-date-picker-message': string;
  'disable-sub-day-granularity-message': string;
  'display-context': DisplayContext;
  'max-history-days': number;
  'project-as-of-date': string;
  'tracked-use-case': string;
}

interface DateRangePresetBarJsxProps {
  attrs: DateRangePresetBarAttrs;
  on?: {
    apply?: (ev: CustomEvent) => void;
    clear?: () => void;
  };
}

const PRESETS_DROPDOWN_DEFAULT_TITLE = ({t}) => t(`reports.date-range.presets.x-to-date`);

export class DateRangePresetBar extends Component<
  DateRangePresetBarState,
  DateRangePresetBarJsxProps['attrs'],
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  private selectedDateRange: null | DateRange = null;
  private customPicker: null | DateRangePicker = null;

  static get attrsSchema(): AttrsSchema<DateRangePresetBarAttrs> {
    return {
      ...super.attrsSchema,
      'date-range': {type: `json`, default: null, description: `Attributes of a valid DateRange instance`},
      'display-context': {type: `string`, default: DisplayContext.FullLength},
      'max-history-days': {type: `number`, default: 0, description: `max days time menu can query into data history`},
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'tracked-use-case': {
        type: `string`,
        description: `For tracking purposes only. Unlike "display-context" which represents a unique configuration that may be reused in multiple places, this attribute directly identifies semantic use cases for distinguishing which parts of the product this component is used in.`,
      },
      'disable-date-picker-message': {type: `string`, default: ``},
      'disable-sub-day-granularity-message': {type: `string`},
      'can-clear': {type: `boolean`, description: `Exposes a control that emits a "clear" event when clicked`},
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
      defaultState: {
        fromTimeText: ``,
        isCustomPickerMenuOpen: false,
        isTimePickerEnabled: false,
        isToDateMenuOpen: false,
        selectedPresetIdx: -1,
        toTimeText: ``,
      },
      contexts: [`currentProject` as const, `queryContextManager` as const, `i18n` as const],
      defaultContexts: {
        queryContextManager: new QueryContextManager(),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    switch (name) {
      case `date-range`: {
        const dateRangeAttrs = JSON.parse(newValue);
        if (DateRange.hasSubDayGranularity(dateRangeAttrs)) {
          this.update({
            fromTimeText: [DateRangeType.Between, DateRangeType.Since].includes(dateRangeAttrs)
              ? getTimeString(dateRangeAttrs.from)
              : ``,
            isTimePickerEnabled: true,
            toTimeText: DateRangeType.Since === dateRangeAttrs ? getTimeString(dateRangeAttrs.to) : ``,
          });
        }
        const dateRangeClass = this.getDateRangeClass();
        const dateRange = dateRangeAttrs && new dateRangeClass(dateRangeAttrs);
        if (dateRange && !dateRange.isValid()) {
          throw new Error(`"date-range" attribute contains invalid DateRange info`);
        }
        this.selectedDateRange = dateRange ?? null;

        // super.attributeChangedCallback() naturally calls this.update();
        this.state.selectedPresetIdx = this.matchSelectedDateRangeToPreset();
        break;
      }
      case `display-context`:
      case `max-history-days`: {
        // super.attributeChangedCallback() naturally calls this.update();
        this.state.selectedPresetIdx = this.matchSelectedDateRangeToPreset();
        break;
      }
    }

    super.attributeChangedCallback(name, oldValue, newValue);
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get print() {
    return this.getContext(`i18n`).print;
  }

  private renderTemplate(): VNode {
    const presetOptions = this.getPresetOptions();
    const noPresetSelected = -1 === this.state.selectedPresetIdx;
    const hasPresets = presetOptions.length > 0;

    const customPickerDateRange = this.getCustomPickerDateRange();
    const hasDateRange = !!customPickerDateRange;
    const toDatePresetOptionsIdx = this.getToDateStartIndex();
    const toDatePresetOptions = presetOptions.slice(toDatePresetOptionsIdx);
    const customSelected = noPresetSelected && hasPresets && hasDateRange;

    return (
      <div sel='.root' role='radiogroup'>
        {this.attr(`disable-date-picker-message`)
          ? this.renderDisableTooltip(this.attr(`disable-date-picker-message`))
          : null}

        {/* Custom selector (opens the custom picker menu) */}
        <div
          sel='.custom-item'
          class={{selected: customSelected}}
          attrs={{
            'aria-checked': Boolean(customSelected).toString(),
            elref: `customItem`,
            role: `radio`,
          }}
        >
          <ButtonEl
            sel='.custom-button'
            attrs={{
              icon: `calendar`,
              theme: ButtonTheme.Tertiary,
              pressed: this.state.isCustomPickerMenuOpen,
              disabled: !!this.attr(`disable-date-picker-message`),
              elref: `customButton`,
            }}
            on={{click: () => this.handleClickCustom()}}
          >
            {this.getDateRangeLabel(customPickerDateRange)}
          </ButtonEl>

          {hasDateRange && !this.attr(`disable-date-picker-message`) ? this.renderTooltip(customPickerDateRange) : null}
        </div>

        {presetOptions.slice(0, toDatePresetOptionsIdx).map((option: PresetOption, idx: number) => {
          const dateRangeClass = this.getDateRangeClass();
          const dateRange = new dateRangeClass(option.value);
          if (!dateRange.isValid()) {
            throw new Error(`"${option.label}" preset is invalid`);
          }
          const disabledReason = this.getDisabledReason(dateRange);
          return (
            <div
              sel='.preset-item'
              class={{selected: idx === this.state.selectedPresetIdx}}
              attrs={{
                'aria-checked': Boolean(idx === this.state.selectedPresetIdx).toString(),
                role: `radio`,
              }}
            >
              <ButtonEl
                attrs={{
                  theme: ButtonTheme.Tertiary,
                  elref: `presetButton`,
                  disabled: Boolean(disabledReason || !!this.attr(`disable-date-picker-message`)),
                }}
                on={{click: () => this.handleClickPreset(option, dateRange)}}
              >
                {this.print(option.shortLabel)}
              </ButtonEl>

              {disabledReason ? (
                <>
                  {/* overlay necessary to cover up the "pointer-events: none" from a disabled <mp-button> that prevents tooltips from showing */}
                  <div sel='.invis-overlay' />
                  <TooltipEl attrs={{placement: Placement.Top, elref: `dateRangeTooltip`}}>{disabledReason}</TooltipEl>
                </>
              ) : !this.attr(`disable-date-picker-message`) ? (
                this.renderTooltip(dateRange)
              ) : null}
            </div>
          );
        })}

        {/* Presets selector (opens the X to date picker menu) */}
        {
          <div
            sel='.preset-item.preset-dropdown-item'
            class={{selected: this.state.selectedPresetIdx >= toDatePresetOptionsIdx}}
          >
            <ButtonEl
              sel='.preset-dropdown-button'
              attrs={{
                disabled: Boolean(this.attr(`disable-date-picker-message`)),
                elref: `presetDropdownButton`,
                pressed: this.state.isToDateMenuOpen,
                theme: ButtonTheme.Tertiary,
              }}
              on={{click: () => this.update({isToDateMenuOpen: true})}}
            >
              <span sel='.preset-dropdown-label'>
                {this.print(this.getPresetsDropdownTitle())}
                <SvgIconEl attrs={{icon: `caret-down`}} />
              </span>
            </ButtonEl>
            {this.renderXToDateTooltip(toDatePresetOptionsIdx)}

            <DropMenuEl
              attrs={{
                elref: `presetDropdownMenu`,
                open: this.state.isToDateMenuOpen,
              }}
              on={{
                change: (ev: CustomEvent) => this.update({isToDateMenuOpen: Visibility.Open === ev.detail.state}),
              }}
            >
              {this.state.isToDateMenuOpen && (
                <ItemsMenuEl
                  attrs={{
                    elref: `presetDropdownMenuItems`,
                  }}
                  props={{
                    sections: [
                      {
                        items: toDatePresetOptions.map((value: PresetOption, _) => {
                          const dateRangeClass = this.getDateRangeClass();
                          const dateRange = new dateRangeClass(value.value);
                          const disabledReason = this.getDisabledReason(dateRange);
                          const [dateRangeText, timezoneText] = getDateRangeTooltipText(
                            dateRange,
                            this.getCurrentProject(),
                          );
                          const tooltipText = disabledReason ?? `${dateRangeText} (${timezoneText})`;

                          return {
                            isDisabled: disabledReason ?? false,
                            label: value.label,
                            tooltip: {text: tooltipText},
                            value,
                          } as SectionItem;
                        }),
                      } as Section,
                    ],
                  }}
                  on={{
                    clickItem: (ev) => {
                      const dateRangeClass = this.getDateRangeClass();
                      this.handleClickPreset(ev.detail.value, new dateRangeClass(ev.detail.value.value));
                      this.update({isToDateMenuOpen: false});
                    },
                  }}
                />
              )}
            </DropMenuEl>
          </div>
        }

        {this.attr(`can-clear`) ? (
          <div
            sel='.preset-item'
            class={{selected: !this.selectedDateRange}}
            attrs={{
              'aria-checked': Boolean(!this.selectedDateRange).toString(),
              role: `radio`,
            }}
          >
            <ButtonEl
              attrs={{theme: ButtonTheme.Tertiary, elref: `clearButton`}}
              on={{click: () => this.handleClickClear()}}
            >
              {this.t(`reports.date-range.presets.default`)}
            </ButtonEl>
            <TooltipEl attrs={{placement: Placement.Top, elref: `dateRangeTooltip`}}>
              {this.t(`reports.date-range.presets.description.default`)}
            </TooltipEl>
          </div>
        ) : null}

        {this.renderCustomPickerMenu(customPickerDateRange)}

        {/* we apply a border to this ".overlapping-outline" element instead of ".root" in order to avoid adding height to this widget */}
        <div sel='.overlapping-outline' />
      </div>
    );
  }

  private renderXToDateTooltip(toDateStartIndex: number): VNode {
    // Don't show the tooltip when the menu is open since it'll look weird.
    if (this.state.isToDateMenuOpen || this.attr(`disable-date-picker-message`)) {
      return null;
    }
    return this.state.selectedPresetIdx >= toDateStartIndex ? (
      this.renderTooltip(this.getCurDateRange())
    ) : (
      <TooltipEl
        attrs={{
          elref: `dateRangeXtdOptionsTooltip`,
          placement: Placement.Top,
          size: DisplaySize.Small,
        }}
        props={{
          // It is important to explicitly null this out, otherwise the content
          // from the other TooltipEl from renderTooltip will be rendered in
          // the tooltip.
          lazyContentRenderer: null,
        }}
      >
        <div sel='.info-tooltip'>
          {this.t(`reports.date-range.presets.description.x-to-date`)}
          {` `}
          <a attrs={{href: `https://docs.mixpanel.com/docs/reports/overview#to-date`, target: `_blank`}}>
            {this.t(`common.learn-more`)}.
          </a>
        </div>
      </TooltipEl>
    );
  }

  private renderTooltip(dateRange: DateRange): VNode {
    return (
      <TooltipEl
        attrs={{
          elref: `dateRangeTooltip`,
          placement: Placement.Top,
        }}
        props={{
          lazyContentRenderer: getDateRangeTooltipRenderer(dateRange, this.getCurrentProject()),
        }}
      />
    );
  }

  private renderDisableTooltip(disableMessage: string): VNode {
    return <TooltipEl attrs={{placement: Placement.Top, elref: `dateRangeTooltip`}}>{disableMessage}</TooltipEl>;
  }

  private renderCustomPickerMenu(dateRange: null | DateRange): VNode {
    return (
      <DropMenuEl
        sel='.custom-picker-menu'
        attrs={{
          'disable-auto-focus': true,
          open: this.state.isCustomPickerMenuOpen,
        }}
        on={{change: (ev: CustomEvent) => this.update({isCustomPickerMenuOpen: Visibility.Open === ev.detail.state})}}
      >
        {this.state.isCustomPickerMenuOpen ? (
          <DateRangePickerEl
            attrs={{
              'display-context': this.attr(`display-context`),
              'date-range': dateRange?.getAttributes() ?? null,
              'disable-sub-day-granularity-message': this.attr(`disable-sub-day-granularity-message`),
              'force-hide-preset-menu': true,
              'from-time-text': this.state.fromTimeText,
              'is-time-picker-enabled': this.state.isTimePickerEnabled,
              'max-history-days': this.attr(`max-history-days`),
              'project-as-of-date': this.attr(`project-as-of-date`),
              'to-time-text': this.state.toTimeText,
              'tracked-use-case': this.attr(`tracked-use-case`) || undefined,
              elref: `dateRangePicker`,
            }}
            on={{
              apply: (ev: CustomEvent) => {
                this.dispatchEvent(
                  new CustomEvent(`apply`, {
                    detail: {dateRange: ev.detail.dateRange},
                  }),
                );
                this.update({
                  fromTimeText: ev.detail.fromTimeText,
                  isCustomPickerMenuOpen: false,
                  isTimePickerEnabled: ev.detail.isTimePickerEnabled,
                  toTimeText: ev.detail.toTimeText,
                });
              },
              cancel: () => this.update({isCustomPickerMenuOpen: false}),
            }}
            hook={{
              insert: (vnode: VNode) => (this.customPicker = vnode.elm as DateRangePicker),
              remove: (_, removeCallback) => {
                this.customPicker = null;
                removeCallback();
              },
            }}
          />
        ) : null}
      </DropMenuEl>
    );
  }

  private getCurDateRange(): DateRange {
    const dateRangeClass = this.getDateRangeClass();
    return new dateRangeClass(this.getPresetOptions()[this.state.selectedPresetIdx].value);
  }

  private getCurrentProject(): CurrentProject | undefined {
    return this.getContext(`currentProject`).get().value;
  }

  private getDateRangeClass(): typeof DateRange {
    const displayContext = this.attr(`display-context`);
    return DATE_RANGE_TYPES[displayContext];
  }

  private getDisabledReason(dateRange: DateRange): string | null {
    const maxHistoryDays = this.attr(`max-history-days`);
    const maxQueryDays = this.getContext(`queryContextManager`)?.getMaxQueryDays();
    if (maxQueryDays && dateRange.getIntervalDays() > maxQueryDays) {
      return this.t(`reports.date-range.disabled.max-days`, {count: maxQueryDays});
    } else if (isMaxHistoryFinite(maxHistoryDays) && !dateRange.isValid(maxHistoryDays)) {
      return this.t(`reports.date-range.disabled.plan-history-limit`, {count: maxHistoryDays});
    }
    return null;
  }

  private getProjectAsOfDate(): moment.Moment {
    const explicitAsOfDate = this.attr(`project-as-of-date`);
    if (explicitAsOfDate) {
      const parsedDate = parseDate(explicitAsOfDate);
      if (!parsedDate) {
        throw new Error(`"project-as-of-date" attribute does not contain a valid date string`);
      }
      return moment(parsedDate);
    }
    return getCurrentMoment({localized: true});
  }

  private getPresetOptions(): PresetOption[] {
    const dateRangeClass = this.getDateRangeClass();
    return dateRangeClass.getPresetOptions();
  }

  private getToDateStartIndex(): number {
    return this.getPresetOptions().findIndex((el) =>
      isEqual(el, this.getDateRangeClass().getPresetOption(PresetDateRange.WeekToDate)),
    );
  }

  private matchSelectedDateRangeToPreset(): number {
    if (this.selectedDateRange) {
      const dateRangeClass = this.getDateRangeClass();
      return this.getPresetOptions().findIndex((option) =>
        this.selectedDateRange.equals(new dateRangeClass(option.value)),
      );
    }
    return -1;
  }

  private getPresetsDropdownTitle(): Translatable {
    return this.state.selectedPresetIdx >= this.getToDateStartIndex()
      ? this.getPresetOptions()[this.state.selectedPresetIdx].shortLabel
      : PRESETS_DROPDOWN_DEFAULT_TITLE;
  }

  private getCustomPickerDateRange(): null | DateRange {
    const noPresetSelected = this.state.selectedPresetIdx === -1;
    if (noPresetSelected && this.selectedDateRange) {
      const dateRangeClass = this.getDateRangeClass();
      return new dateRangeClass(this.selectedDateRange);
    }

    // if a preset is selected, we intentionally treat the custom picker as if it was empty
    return null;
  }

  private getDateRangeLabel(dateRange: null | DateRange): string {
    if (!dateRange) {
      // the getDateRangeLabel() utility method already handles nulls, but we want a different default label in this widget
      return this.t(`reports.date-range.presets.custom`);
    }
    const noPresetSelected = this.state.selectedPresetIdx === -1;
    const matchingPreset = noPresetSelected ? null : this.getPresetOptions()[this.state.selectedPresetIdx];
    return this.print(
      getDateRangeLabel({
        dateRange,
        matchingPreset,
        projectAsOfDate:
          null /* project as of date; we set this to "null" because we're trialing a change where we show the project-as-of-date in the tooltip, not the label */,
        now: getCurrentMoment({localized: true}),
      }),
    );
  }

  private handleClickCustom(): void {
    this.update({isCustomPickerMenuOpen: true});
    requestAnimationFrame(() => this.customPicker?.customScrollIntoViewIfNeeded());
  }

  private handleClickPreset(presetOption: PresetOption, dateRange: DateRange) {
    this.dispatchEvent(new CustomEvent(`apply`, {detail: {dateRange}}));

    this.track(`[mp-query-builder] Select date preset`, {
      ...generateDateRangeTrackingProps(
        dateRange,
        getReportName(),
        getDatePickerMode(dateRange),
        this.getProjectAsOfDate(),
        this.attr(`tracked-use-case`),
      ),
      'Preset name': this.print(presetOption.label, `en`),
      'Preset source': `mixpanel`, // TODO: move to an enum if/when there are additional sources for presets (eg. user defined presets)
    });
  }

  private handleClickClear() {
    this.dispatchEvent(new CustomEvent(`clear`));
  }

  private track(event: string, properties: {[index: string]: any}): void {
    emitTrackEvent(this, {eventName: event, props: properties, eventPrefix: null, cancelable: true});
  }
}

export const DateRangePresetBarEl = defineAndMakeJsxFactory<DateRangePresetBarJsxProps>(
  `mp-date-range-preset-bar`,
  DateRangePresetBar,
);
export const DateRangePresetBarReact = panel2react<DateRangePresetBarJsxProps>(
  `mp-date-range-preset-bar`,
  DateRangePresetBar,
);
